import { useIssues } from "@/lib/issues"
import { Badge, DateRange, FilterBar, FilterSelect } from "@/terabizz"
import { AddressText } from "@/components/address-fields"
import { EllipsisHorizontal } from "@medusajs/icons"
import { Button, Checkbox, Drawer, DropdownMenu, Input, Prompt, Select, Text, Textarea, toast } from "@medusajs/ui"
import { useColumns, ViewTabs } from "@/components/list-views"
import { ReturnsCard } from "@/components/order-returns"
import { OrderRiskCard } from "@/components/order-risk"
import { OrderShipStationCard } from "@/components/shipstation"
import { OrderLotsCard } from "@/components/order-lots"
import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { sdk } from "@/lib/sdk"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { downloadExport } from "@/lib/export"
import { TagsDrawer } from "@/routes/products"
import { DataTable, EmptyState, ErrorState, PageHeader, SearchInput, Section, Status, Thumb, usePage } from "@/components/ui"
import { useActivity } from "@/lib/api"
import { formatDate, formatMoney, fullName } from "@/lib/format"
import {
  CARRIERS,
  guessCarrier,
  useAddComment,
  useAddTracking,
  useArchiveOrder,
  useCancelFulfillment,
  useCancelOrder,
  useCapture,
  useMarkPaid,
  useFulfill,
  useMarkDelivered,
  useBulkOrders,
  useOrderDetail,
  useOrderList,
  useRefund,
  useUpdateOrderMeta,
  useOrderSubscriptions,
  channelOf,
  kindOf,
  CHANNEL_LABEL,
  KIND_LABEL,
  type OrderKind,
  type OrderTab,
} from "@/lib/orders"
import { useCan, useOrderName } from "@/lib/permissions"
import type { SettlementStamp } from "@/lib/settlements"
import { useStoreTz } from "@/analytics/pickers"
import { errorMessage } from "@/lib/sdk"

const PAGE = 50
const TABS: { value: OrderTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "unfulfilled", label: "Unfulfilled" },
  { value: "unpaid", label: "Unpaid" },
  { value: "open", label: "Open" },
  { value: "archived", label: "Archived" },
]

/** One-time / Subscription / Renewal, so staff see at a glance what an order is. */
export function KindBadge({ kind }: { kind: OrderKind | undefined }) {
  if (!kind) return null
  return <Badge tone={kind === "subscription" ? "magic" : kind === "renewal" ? "info" : "neutral"}>{KIND_LABEL[kind]}</Badge>
}

/** Shopify-style metrics strip above the orders table (today). */
function OrdersMetrics({ counts }: { counts?: Record<string, number> }) {
  // The Home numbers for today (sales ledger, the same as Reports), so the three always agree.
  const { data } = useQuery({
    queryKey: ["dashboard", "today", "orders-strip"],
    staleTime: 60_000,
    queryFn: () => sdk.client.fetch<{ kpis: { total_sales: { value: number }; orders: { value: number }; aov: { value: number } } }>("/admin/terify/dashboard", { query: { range: "today" } }),
  })
  const k = data?.kpis
  const cells: [string, string][] = [
    ["Orders today", k ? String(k.orders.value) : "—"],
    ["Total sales today", k ? formatMoney(k.total_sales.value) : "—"],
    ["Average order value", k ? formatMoney(k.aov.value) : "—"],
    ["To fulfill", counts ? String(counts.unfulfilled ?? 0) : "—"],
  ]
  return (
    // Keel KPI strip: surface-sunken, mono figures, the headline (orders today) in keel-blue.
    <div className="mb-3 grid grid-cols-2 divide-x divide-y-0 divide-[var(--k-line)] rounded-[var(--k-r-md)] bg-[var(--k-sunken)] shadow-[inset_0_0_0_1px_var(--k-line)] sm:grid-cols-4 [&>div:nth-child(3)]:border-t sm:[&>div:nth-child(3)]:border-t-0 [&>div:nth-child(4)]:border-t sm:[&>div:nth-child(4)]:border-t-0">
      {cells.map(([l, v], i) => (
        <div key={l} className="min-w-0 border-[var(--k-line)] px-4 py-3">
          <div className="k-eyebrow truncate">{l}</div>
          <div className={"k-num mt-0.5 text-[18px] font-medium leading-6" + (i === 0 ? " text-[var(--k-blue)]" : "")}>{v}</div>
        </div>
      ))}
    </div>
  )
}

export function OrdersPage() {
  const orderName = useOrderName()
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const tab = (params.get("tab") as OrderTab) || "all"
  const facet = (k: string) => params.get(k) ?? ""
  const facets = { payment: facet("payment"), fulfillment: facet("fulfillment"), type: facet("type"), channel: facet("channel"), from: facet("from"), to: facet("to"), sort: facet("sort") }
  const { data, isLoading, error } = useOrderList({ tab, q, ...facets, limit: PAGE, offset: pageIndex * PAGE })
  const navigate = useNavigate()
  const can = useCan()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [tagMode, setTagMode] = useState<"add" | "remove" | null>(null)
  const bulk = useBulkOrders()
  const run = (body: Parameters<typeof bulk.mutate>[0], msg: string) =>
    bulk.mutate(body, { onSuccess: () => (toast.success(msg), setSelected(new Set())), onError: (e) => toast.error(errorMessage(e)) })
  const setFacet = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v && !(k === "sort" && v === "newest") ? next.set(k, v) : next.delete(k)
    next.delete("page")
    setSelected(new Set())
    setParams(next)
  }
  const filtered = !!(facets.payment || facets.fulfillment || facets.type || facets.channel || facets.from || facets.to)
  const cols = useColumns("orders", [
    { key: "date", label: "Date" },
    { key: "customer", label: "Customer" },
    { key: "type", label: "Type" },
    { key: "total", label: "Total" },
    { key: "payment", label: "Payment status" },
    { key: "fulfillment", label: "Fulfillment status" },
    { key: "items", label: "Items" },
    { key: "delivery", label: "Delivery method" },
    { key: "channel", label: "Channel", default: false },
    { key: "tags", label: "Tags", default: false },
    { key: "discount", label: "Discount code", default: false },
    { key: "email", label: "Email", default: false },
  ])


  return (
    <>
      <PageHeader title="Orders" actions={<Button size="small" variant="secondary" onClick={() => downloadExport("/admin/terify/orders/export", { tab, ...(q ? { q } : {}), ...Object.fromEntries(Object.entries({ payment: facets.payment, fulfillment: facets.fulfillment, type: facets.type, channel: facets.channel, from: facets.from, to: facets.to }).filter(([, v]) => v)) })}>Export</Button>} />
      <OrdersMetrics counts={data?.counts} />
      <Section>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <ViewTabs
            resource="orders"
            canSave={can("orders:write")}
            params={params}
            setParams={(p) => (setSelected(new Set()), setParams(p))}
            tabs={TABS.map((t) => ({
              value: t.value,
              params: (t.value === "all" ? {} : { tab: t.value }) as Record<string, string>,
              label: (
                <>
                  {t.label}
                  {data?.counts && t.value !== "all" && t.value !== "archived" && data.counts[t.value] > 0 ? <span className="ml-1 text-ui-fg-muted">{data.counts[t.value]}</span> : null}
                </>
              ),
            }))}
          />
          <div className="flex items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search order #, email or customer" />
          {cols.menu}
          </div>
        </div>
        <FilterBar>
          <FilterSelect label="Payment status" value={facets.payment} onChange={(v) => setFacet({ payment: v })} options={[["paid", "Paid"], ["unpaid", "Payment pending"], ["refunded", "Refunded"]]} />
          <FilterSelect label="Fulfillment status" value={facets.fulfillment} onChange={(v) => setFacet({ fulfillment: v })} options={[["unfulfilled", "Unfulfilled"], ["partial", "Partially fulfilled"], ["fulfilled", "Fulfilled"]]} />
          <FilterSelect label="Type" value={facets.type} onChange={(v) => setFacet({ type: v })} options={[["one_time", "One-time"], ["any_subscription", "Any subscription"], ["subscription", "New subscription"], ["renewal", "Subscription renewal"]]} />
          <FilterSelect label="Channel" value={facets.channel} onChange={(v) => setFacet({ channel: v })} options={[["online_store", "Online Store"], ["draft_order", "Draft order"], ["subscriptions", "Subscriptions app"]]} />
          <DateRange from={facets.from} to={facets.to} onChange={(p) => setFacet(p)} />
          {filtered && <Button size="small" variant="transparent" onClick={() => setFacet({ payment: "", fulfillment: "", type: "", channel: "", from: "", to: "" })}>Clear filters</Button>}
          <div className="ml-auto"><FilterSelect label="Sort" value={facets.sort || "newest"} onChange={(v) => setFacet({ sort: v })} allowAll={false} options={[["newest", "Newest first"], ["oldest", "Oldest first"], ["total_desc", "Total: high to low"], ["total_asc", "Total: low to high"]]} /></div>
        </FilterBar>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(o) => o.id}
            rows={data?.orders}
            count={data?.count ?? 0}
            pageSize={PAGE}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(o) => navigate(`/orders/${o.id}`)}
            selected={can("orders:write") ? selected : undefined}
            onSelectedChange={can("orders:write") ? setSelected : undefined}
            bulkActions={
              <>
                <Button size="small" variant="secondary" isLoading={bulk.isPending} onClick={() => run({ ids: [...selected], action: "archive" }, "Orders archived")}>Archive</Button>
                <Button size="small" variant="secondary" isLoading={bulk.isPending} onClick={() => run({ ids: [...selected], action: "unarchive" }, "Orders unarchived")}>Unarchive</Button>
                <Button size="small" variant="secondary" onClick={() => setTagMode("add")}>Add tags</Button>
                <Button size="small" variant="secondary" onClick={() => setTagMode("remove")}>Remove tags</Button>
              </>
            }
            empty={<EmptyState title={q ? "No orders match your search" : tab === "all" ? "No orders yet" : "No orders here"} body={tab === "all" && !q ? "Orders placed on your store appear here." : undefined} />}
            mobile={(o) => (
              <div className="flex flex-col gap-y-1">
                <div className="flex items-center justify-between gap-x-2">
                  <span className="k-data font-medium">{orderName(o.display_id)}</span>
                  <span className="k-data">{formatMoney(o.total, o.currency_code)}</span>
                </div>
                <div className="flex items-center justify-between gap-x-2 text-[12px] text-ui-fg-subtle">
                  <span className="truncate">{o.customer_name || o.email || "No customer"}</span>
                  <span className="k-data shrink-0">{formatDate(o.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  <KindBadge kind={o.kind} />
                  <Status value={o.payment_status} />
                  <Status value={o.status === "canceled" ? "canceled" : o.fulfillment_status} />
                </div>
              </div>
            )}
            columns={[
              { key: "id", header: "Order", cell: (o) => <span className="k-data font-medium">{orderName(o.display_id)}</span> },
              ...[
                { key: "date", header: "Date", cell: (o: any) => <span className="k-data">{formatDate(o.created_at)}</span> },
                { key: "customer", header: "Customer", cell: (o: any) => o.customer_name || o.email || "No customer" },
                { key: "type", header: "Type", cell: (o: any) => <KindBadge kind={o.kind} /> },
                { key: "email", header: "Email", cell: (o: any) => <span className="text-ui-fg-subtle">{o.email ?? ""}</span> },
                { key: "total", header: "Total", cell: (o: any) => <span className="k-data">{formatMoney(o.total, o.currency_code)}</span> },
                { key: "payment", header: "Payment status", cell: (o: any) => <Status value={o.payment_status} /> },
                { key: "fulfillment", header: "Fulfillment status", cell: (o: any) => <Status value={o.status === "canceled" ? "canceled" : o.fulfillment_status} /> },
                { key: "items", header: "Items", cell: (o: any) => <span className="whitespace-nowrap text-ui-fg-subtle"><span className="k-data">{o.item_count}</span> item{o.item_count === 1 ? "" : "s"}</span> },
                { key: "delivery", header: "Delivery method", cell: (o: any) => <span className="text-ui-fg-subtle">{o.delivery_method || "—"}</span> },
                { key: "channel", header: "Channel", cell: (o: any) => <span className="text-ui-fg-subtle">{CHANNEL_LABEL[o.channel as keyof typeof CHANNEL_LABEL] ?? "Online Store"}</span> },
                { key: "tags", header: "Tags", cell: (o: any) => <span className="text-ui-fg-subtle">{(o.tags ?? []).join(", ") || "—"}</span> },
                { key: "discount", header: "Discount code", cell: (o: any) => <span className="text-ui-fg-subtle">{o.discount_codes || "—"}</span> },
              ].filter((c) => cols.isOn(c.key)),
            ]}
          />
        )}
      </Section>
      {tagMode && <TagsDrawer noun="order" mode={tagMode} count={selected.size} busy={bulk.isPending} onClose={() => setTagMode(null)} onApply={(tags) => (run({ ids: [...selected], action: tagMode === "add" ? "add_tags" : "remove_tags", tags }, tagMode === "add" ? "Tags added" : "Tags removed"), setTagMode(null))} />}
    </>
  )
}

// ── Order page ─────────────────────────────────────────────────────────────

const qtyLeft = (i: any) => Number(i.quantity) - Number(i.detail?.fulfilled_quantity ?? 0)

export function OrderDetailPage() {
  const orderName = useOrderName()
  const { id = "" } = useParams()
  const { data, error } = useOrderDetail(id)
  const navigate = useNavigate()
  const can = useCan()
  const o: any = data?.order
  const [fulfilling, setFulfilling] = useState(false)
  const [refunding, setRefunding] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const archive = useArchiveOrder(id)
  const cancel = useCancelOrder(id)
  const capture = useCapture(id)
  const markPaid = useMarkPaid(id)

  if (error) return <ErrorState error={error} />
  if (!o) return null

  const canWrite = can("orders:write")
  const canRefund = can("refunds:write")
  const payments: any[] = (o.payment_collections ?? []).flatMap((pc: any) => pc.payments ?? [])
  const captured = payments.reduce((s, p) => s + (p.captures ?? []).reduce((t: number, c: any) => t + Number(c.amount), 0), 0)
  const refunded = payments.reduce((s, p) => s + (p.refunds ?? []).reduce((t: number, r: any) => t + Number(r.amount), 0), 0)
  const authorized = payments.find((p) => !p.captured_at && !p.canceled_at)
  const capturedPayment = payments.find((p) => p.captured_at)
  const unfulfilled = (o.items ?? []).filter((i: any) => qtyLeft(i) > 0)
  const fulfillments = (o.fulfillments ?? []).filter((f: any) => !f.canceled_at)
  const archived = o.metadata?.terify_archived === true || o.status === "archived" || o.status === "completed"
  const canceled = o.status === "canceled"
  const cur = o.currency_code

  return (
    <div className="pb-10">
      <PageHeader
        back="/orders"
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-[family-name:var(--k-font-mono)] font-medium tracking-[-0.02em]">{orderName(o.display_id)}</span>
            <KindBadge kind={kindOf(o)} />
            <Status value={o.payment_status} />
            <Status value={canceled ? "canceled" : o.fulfillment_status} />
            {archived && <Status value="archived" />}
          </span>
        }
        subtitle={`${formatDate(o.created_at)} from ${CHANNEL_LABEL[channelOf(o)]}`}
        actions={
          <>
            {canRefund && captured - refunded > 0 && (
              <Button size="small" variant="secondary" onClick={() => setRefunding(true)}>
                Refund
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button size="small" variant="secondary">Print</Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item onClick={() => window.open(`/orders/${o.id}/print?type=slip`, "_blank", "noopener")}>Print packing slip</DropdownMenu.Item>
                <DropdownMenu.Item onClick={() => window.open(`/orders/${o.id}/print?type=invoice`, "_blank", "noopener")}>Print invoice</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
            {canWrite && (
              <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                  <Button size="small" variant="secondary">
                    More actions <EllipsisHorizontal />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  {!canceled && <DropdownMenu.Item onClick={() => navigate(`/orders/${o.id}/edit`)}>Edit order</DropdownMenu.Item>}
                  <DropdownMenu.Item
                    onClick={() =>
                      archive.mutate(!archived, { onSuccess: () => toast.success(archived ? "Order unarchived" : "Order archived"), onError: (e) => toast.error(errorMessage(e)) })
                    }
                  >
                    {archived ? "Unarchive" : "Archive"}
                  </DropdownMenu.Item>
                  {!canceled && fulfillments.length === 0 && (
                    <>
                      <DropdownMenu.Separator />
                      <DropdownMenu.Item className="text-ui-fg-error" onClick={() => setConfirmCancel(true)}>
                        Cancel order
                      </DropdownMenu.Item>
                    </>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu>
            )}
          </>
        }
      />

      <OrderIssues orderId={o.id} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          {unfulfilled.length > 0 && !canceled && (
            <Section
              title={
                <span className="flex items-center gap-x-2">
                  <Status value="not_fulfilled" /> <span className="text-ui-fg-subtle">({unfulfilled.reduce((s: number, i: any) => s + qtyLeft(i), 0)})</span>
                </span>
              }
            >
              <ItemList items={unfulfilled} qty={qtyLeft} currency={cur} />
              {canWrite && (
                <div className="flex justify-end px-4 py-3">
                  <Button size="small" onClick={() => setFulfilling(true)}>
                    Fulfill items
                  </Button>
                </div>
              )}
            </Section>
          )}

          {fulfillments.map((f: any) => (
            <FulfillmentCard key={f.id} order={o} f={f} canWrite={canWrite} />
          ))}

          <Section title={<Status value={o.payment_status} />}>
            <dl className="flex flex-col gap-y-2 px-4 py-3 text-[13px]">
              <Row label="Subtotal" sub={(() => { const n = (o.items ?? []).reduce((s: number, i: any) => s + Number(i.quantity), 0); return `${n} item${n === 1 ? "" : "s"}` })()} value={formatMoney(o.item_total ?? o.subtotal, cur)} />
              {Number(o.discount_total) > 0 && <Row label="Discount" sub={discountCodes(o).join(", ") || undefined} value={`−${formatMoney(o.discount_total, cur)}`} />}
              <Row label="Shipping" sub={o.shipping_methods?.map((m: any) => m.name).join(", ")} value={formatMoney(o.shipping_total, cur)} />
              <Row label="Taxes" value={formatMoney(o.tax_total, cur)} />
              <Row label="Total" value={formatMoney(orderTotal(o), cur)} strong />
            </dl>
            <dl className="flex flex-col gap-y-2 px-4 py-3 text-[13px]">
              <Row label="Paid" value={formatMoney(captured, cur)} />
              {refunded > 0 && <Row label="Refunded" value={`−${formatMoney(refunded, cur)}`} />}
              <Row label="Net payment" value={formatMoney(captured - refunded, cur)} strong />
              {authorized && !canceled && <Row label="Authorized, not captured" value={formatMoney(authorized.amount, cur)} />}
              {!canceled && !authorized && Number(o.summary?.pending_difference ?? 0) > 0.004 && <Row label="Balance due" value={formatMoney(o.summary.pending_difference, cur)} strong />}
              {!canceled && Number(o.summary?.pending_difference ?? 0) < -0.004 && captured > 0 && <Row label="Refund owed" value={formatMoney(-o.summary.pending_difference, cur)} />}
            </dl>
            {!canceled && !authorized && canWrite && Number(o.summary?.pending_difference ?? 0) > 0.004 && (
              <div className="flex items-center justify-between gap-x-3 px-4 py-3">
                <Text size="small" className="text-ui-fg-subtle">Got the money another way (cash, bank transfer)? Record it.</Text>
                <Button
                  size="small"
                  variant="secondary"
                  isLoading={markPaid.isPending}
                  onClick={() => markPaid.mutate({ order: o, amount: Number(o.summary.pending_difference) }, { onSuccess: () => toast.success("Marked as paid"), onError: (e) => toast.error(errorMessage(e)) })}
                >
                  Mark as paid
                </Button>
              </div>
            )}
            {payments.filter((p) => p.provider_id === "pp_authorizenet_authorizenet" && p.data?.transaction_id).map((p) => (
              <div key={p.id} className="flex flex-col gap-y-1 px-4 py-3 text-[13px]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>{p.data.card?.brand ?? "Card"} •••• {p.data.card?.last4 ?? "????"} <span className="text-ui-fg-subtle">· Authorize.net{p.data.env === "sandbox" ? " (test)" : ""}</span></span>
                  {p.data.held_for_review ? <span className="rounded-md bg-ui-tag-orange-bg px-1.5 py-0.5 text-[12px] text-ui-tag-orange-text">Held for review in Authorize.net</span> : null}
                </div>
                <span className="text-[12px] text-ui-fg-subtle">
                  Transaction {p.data.transaction_id}{p.data.auth_code ? ` · auth ${p.data.auth_code}` : ""} · {avsLabel(p.data.avs)} · {cvvLabel(p.data.cvv)}
                </span>
                <SettlementLine stamp={o.metadata?.anet_settlement} captured={!!p.captured_at || !!p.data.captured} voided={!!p.data.voided} />
              </div>
            ))}
            {authorized && !canceled && canWrite && (
              <div className="flex items-center justify-between gap-x-3 px-4 py-3">
                <Text size="small" className="text-ui-fg-subtle">
                  Capture the authorized payment to charge the customer.
                </Text>
                <Button
                  size="small"
                  isLoading={capture.isPending}
                  onClick={() => capture.mutate(authorized.id, { onSuccess: () => toast.success("Payment captured"), onError: (e) => toast.error(errorMessage(e)) })}
                >
                  Capture payment
                </Button>
              </div>
            )}
          </Section>

          {!canceled && <ReturnsCard order={o} canWrite={canWrite} />}
          <Timeline order={o} canWrite={canWrite} />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <SubscriptionCard order={o} />
          <NotesCard order={o} canWrite={canWrite} />
          <CustomerCard order={o} />
          <AttributionCard order={o} />
          <OrderShipStationCard orderId={o.id} canceled={canceled} />
          {!canceled && <OrderLotsCard orderId={o.id} />}
          <OrderRiskCard order={o} />
          <TagsCard order={o} canWrite={canWrite} />
        </div>
      </div>

      {fulfilling && <FulfillDrawer order={o} items={unfulfilled} onClose={() => setFulfilling(false)} />}
      {refunding && capturedPayment && <RefundDrawer order={o} paymentId={capturedPayment.id} max={captured - refunded} onClose={() => setRefunding(false)} />}

      <Prompt open={confirmCancel} onOpenChange={setConfirmCancel}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Cancel order {orderName(o.display_id)}?</Prompt.Title>
            <Prompt.Description>The authorization is released and items go back to stock. This can't be undone.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep order</Prompt.Cancel>
            <Prompt.Action onClick={() => cancel.mutate(undefined, { onSuccess: () => toast.success("Order canceled"), onError: (e) => toast.error(errorMessage(e)) })}>
              Cancel order
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  )
}

function Row({ label, sub, value, strong }: { label: string; sub?: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-x-4">
      <div className="flex min-w-0 gap-x-6">
        <dt className={strong ? "font-semibold" : ""}>{label}</dt>
        {sub && <dd className="truncate text-ui-fg-subtle">{sub}</dd>}
      </div>
      <dd className={"k-data shrink-0 " + (strong ? "font-medium" : "")}>{value}</dd>
    </div>
  )
}

function ItemList({ items, qty, currency }: { items: any[]; qty: (i: any) => number; currency: string }) {
  return (
    <ul className="divide-y">
      {items.map((i) => (
        <li key={i.id} className="flex items-center gap-x-3 px-4 py-3">
          <Thumb src={i.thumbnail} />
          <div className="min-w-0 flex-1">
            {i.product_id ? (
              <Link to={`/products/${i.product_id}`} className="block truncate text-[13px] font-medium text-ui-fg-interactive hover:underline">
                {i.product_title ?? i.title}
              </Link>
            ) : (
              <Text size="small" weight="plus" className="truncate">{i.product_title ?? i.title}</Text>
            )}
            <Text size="xsmall" className="text-ui-fg-subtle">
              {[i.variant_title && i.variant_title !== "Default Title" ? i.variant_title : null, i.variant_sku && `SKU: ${i.variant_sku}`].filter(Boolean).join(" · ")}
            </Text>
            {subLabel(i) && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-ui-tag-purple-bg px-1.5 py-0.5 text-[11px] font-medium text-ui-tag-purple-text">↻ {subLabel(i)}</span>
            )}
          </div>
          <Text size="small" className="k-data shrink-0 text-ui-fg-subtle">
            {formatMoney(i.unit_price, currency)} × {qty(i)}
          </Text>
          <Text size="small" className="k-data hidden w-20 shrink-0 text-right sm:block">
            {formatMoney(Number(i.unit_price) * qty(i), currency)}
          </Text>
        </li>
      ))}
    </ul>
  )
}

function FulfillmentCard({ order, f, canWrite }: { order: any; f: any; canWrite: boolean }) {
  const delivered = useMarkDelivered(order.id)
  const cancel = useCancelFulfillment(order.id)
  const [tracking, setTracking] = useState(false)
  const label = f.labels?.[0]
  const state = f.delivered_at ? "delivered" : f.shipped_at ? "shipped" : "fulfilled"
  const items = (f.items ?? []).map((fi: any) => {
    const li = order.items.find((i: any) => i.id === fi.line_item_id) ?? {}
    return { ...li, id: fi.line_item_id, _qty: Number(fi.quantity) }
  })
  return (
    <Section
      title={
        <span className="flex items-center gap-x-2">
          <Status value={state} /> <span className="text-ui-fg-subtle">({items.reduce((s: number, i: any) => s + i._qty, 0)})</span>
        </span>
      }
      actions={
        canWrite &&
        !f.delivered_at && (
          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <Button size="small" variant="transparent" aria-label="Fulfillment actions">
                <EllipsisHorizontal />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              {!label && <DropdownMenu.Item onClick={() => setTracking(true)}>Add tracking</DropdownMenu.Item>}
              <DropdownMenu.Item onClick={() => delivered.mutate(f.id, { onSuccess: () => toast.success("Marked as delivered"), onError: (e) => toast.error(errorMessage(e)) })}>
                Mark as delivered
              </DropdownMenu.Item>
              {!f.shipped_at && (
                <DropdownMenu.Item className="text-ui-fg-error" onClick={() => cancel.mutate(f.id, { onSuccess: () => toast.success("Fulfillment canceled"), onError: (e) => toast.error(errorMessage(e)) })}>
                  Cancel fulfillment
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        )
      }
    >
      {label?.tracking_number && (
        <div className="px-4 py-2 text-[13px]">
          <span className="text-ui-fg-subtle">Tracking: </span>
          {label.tracking_url && label.tracking_url !== "#" ? (
            <a href={label.tracking_url} target="_blank" rel="noreferrer noopener" className="text-ui-fg-interactive hover:underline">
              {label.tracking_number}
            </a>
          ) : (
            label.tracking_number
          )}
        </div>
      )}
      <ItemList items={items} qty={(i) => i._qty} currency={order.currency_code} />
      {tracking && <TrackingDrawer order={order} f={f} items={items.map((i: any) => ({ id: i.id, quantity: i._qty }))} onClose={() => setTracking(false)} />}
    </Section>
  )
}

function TrackingFields({ number, setNumber, carrier, setCarrier, url, setUrl }: { number: string; setNumber: (v: string) => void; carrier: string; setCarrier: (v: string) => void; url: string; setUrl: (v: string) => void }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Tracking number">
        <Input
          value={number}
          onChange={(e) => {
            setNumber(e.target.value)
            setCarrier(guessCarrier(e.target.value))
          }}
        />
      </Field>
      <Field label="Shipping carrier">
        <Select value={carrier} onValueChange={setCarrier}>
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {CARRIERS.map((c) => (
              <Select.Item key={c.value} value={c.value}>
                {c.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Field>
      {carrier === "other" && (
        <div className="sm:col-span-2">
          <Field label="Tracking URL">
            <Input value={url} placeholder="https://" onChange={(e) => setUrl(e.target.value)} />
          </Field>
        </div>
      )}
    </div>
  )
}

const trackingUrl = (carrier: string, number: string, custom: string) => {
  if (!number.trim()) return undefined
  if (carrier === "other") return /^https:\/\//i.test(custom.trim()) ? custom.trim() : undefined
  return CARRIERS.find((c) => c.value === carrier)?.url(encodeURIComponent(number.trim()))
}

function FulfillDrawer({ order, items, onClose }: { order: any; items: any[]; onClose: () => void }) {
  const fulfill = useFulfill(order.id)
  const [qty, setQty] = useState<Record<string, number>>(() => Object.fromEntries(items.map((i) => [i.id, qtyLeft(i)])))
  const [number, setNumber] = useState("")
  const [carrier, setCarrier] = useState("usps")
  const [url, setUrl] = useState("")
  const [notify, setNotify] = useState(true)
  const total = Object.values(qty).reduce((s, n) => s + n, 0)
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Fulfill items</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-5 overflow-y-auto">
          <ul className="divide-y rounded-lg border">
            {items.map((i) => (
              <li key={i.id} className="flex items-center gap-x-3 px-3 py-2">
                <Thumb src={i.thumbnail} size={36} />
                <div className="min-w-0 flex-1">
                  <Text size="small" weight="plus" className="truncate">{i.product_title ?? i.title}</Text>
                  {i.variant_title && i.variant_title !== "Default Title" && <Text size="xsmall" className="text-ui-fg-subtle">{i.variant_title}</Text>}
                </div>
                <div className="flex items-center gap-x-1">
                  <Input
                    size="small"
                    className="w-16"
                    inputMode="numeric"
                    value={String(qty[i.id] ?? 0)}
                    onChange={(e) => setQty((q) => ({ ...q, [i.id]: Math.max(0, Math.min(qtyLeft(i), Number(e.target.value.replace(/\D/g, "")) || 0)) }))}
                  />
                  <Text size="small" className="text-ui-fg-subtle">of {qtyLeft(i)}</Text>
                </div>
              </li>
            ))}
          </ul>
          <TrackingFields number={number} setNumber={setNumber} carrier={carrier} setCarrier={setCarrier} url={url} setUrl={setUrl} />
          <label className="flex items-center gap-x-2">
            <Checkbox checked={notify} onCheckedChange={(c) => setNotify(c === true)} />
            <Text size="small">Send shipment details to your customer now</Text>
          </label>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button
            size="small"
            disabled={!total}
            isLoading={fulfill.isPending}
            onClick={() =>
              fulfill.mutate(
                {
                  items: Object.entries(qty).filter(([, n]) => n > 0).map(([id, quantity]) => ({ id, quantity })),
                  tracking_number: number.trim() || undefined,
                  tracking_url: trackingUrl(carrier, number, url),
                  notify,
                },
                {
                  onSuccess: () => {
                    toast.success(`Fulfilled ${total} item${total === 1 ? "" : "s"}`)
                    onClose()
                  },
                  onError: (e) => toast.error(errorMessage(e)),
                }
              )
            }
          >
            Fulfill {total} item{total === 1 ? "" : "s"}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

function TrackingDrawer({ order, f, items, onClose }: { order: any; f: any; items: { id: string; quantity: number }[]; onClose: () => void }) {
  const add = useAddTracking(order.id)
  const [number, setNumber] = useState("")
  const [carrier, setCarrier] = useState("usps")
  const [url, setUrl] = useState("")
  const [notify, setNotify] = useState(true)
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Add tracking</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-5">
          <TrackingFields number={number} setNumber={setNumber} carrier={carrier} setCarrier={setCarrier} url={url} setUrl={setUrl} />
          <label className="flex items-center gap-x-2">
            <Checkbox checked={notify} onCheckedChange={(c) => setNotify(c === true)} />
            <Text size="small">Send shipment details to your customer now</Text>
          </label>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button
            size="small"
            disabled={!number.trim()}
            isLoading={add.isPending}
            onClick={() =>
              add.mutate(
                { fulfillmentId: f.id, items, tracking_number: number.trim(), tracking_url: trackingUrl(carrier, number, url), notify },
                { onSuccess: () => (toast.success("Tracking added"), onClose()), onError: (e) => toast.error(errorMessage(e)) }
              )
            }
          >
            Save
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

function RefundDrawer({ order, paymentId, max, onClose }: { order: any; paymentId: string; max: number; onClose: () => void }) {
  const refund = useRefund(order.id)
  const [amount, setAmount] = useState(max.toFixed(2))
  const [note, setNote] = useState("")
  const n = Number(amount)
  const invalid = !(n > 0) || n > max + 0.0001
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Refund</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4">
          <Field label="Refund amount" hint={`${formatMoney(max, order.currency_code)} available for refund`}>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} />
          </Field>
          <Field label="Reason for refund" hint="Only you and other staff can see this reason.">
            <Textarea rows={3} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button
            size="small"
            disabled={invalid}
            isLoading={refund.isPending}
            onClick={() =>
              refund.mutate(
                { paymentId, amount: Math.round(n * 100) / 100, note: note.trim() || undefined },
                { onSuccess: () => (toast.success(`Refunded ${formatMoney(n, order.currency_code)}`), onClose()), onError: (e) => toast.error(errorMessage(e)) }
              )
            }
          >
            Refund {invalid ? "" : formatMoney(n, order.currency_code)}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

/** Shopify-style timeline: order events derived from the order + staff comments and changes from the audit log. */
function Timeline({ order, canWrite }: { order: any; canWrite: boolean }) {
  const { data } = useActivity({ resource_id: order.id, limit: 100, offset: 0 })
  const add = useAddComment(order.id)
  const [text, setText] = useState("")
  const events = useMemo(() => {
    const out: { at: string; text: string; by?: string | null; comment?: boolean }[] = []
    const cur = order.currency_code
    const ch = channelOf(order)
    out.push({ at: order.created_at, text: ch === "subscriptions" ? `Subscription renewal charged automatically${order.email ? ` for ${order.email}` : ""}.` : `Order placed from ${CHANNEL_LABEL[ch]}${order.email ? ` by ${order.email}` : ""}.` })
    for (const pc of order.payment_collections ?? [])
      for (const p of pc.payments ?? []) {
        out.push({ at: p.created_at, text: `A ${formatMoney(p.amount, cur)} payment was authorized.` })
        for (const c of p.captures ?? []) out.push({ at: c.created_at, text: `A ${formatMoney(c.amount, cur)} payment was captured.`, by: c.created_by ? null : null })
        for (const r of p.refunds ?? []) out.push({ at: r.created_at, text: `A ${formatMoney(r.amount, cur)} refund was processed${r.note ? `: ${r.note}` : ""}.` })
      }
    for (const f of order.fulfillments ?? []) {
      const n = (f.items ?? []).reduce((s: number, i: any) => s + Number(i.quantity), 0)
      out.push({ at: f.created_at, text: `${n} item${n === 1 ? " was" : "s were"} fulfilled.` })
      if (f.shipped_at) out.push({ at: f.shipped_at, text: `Shipped${f.labels?.[0]?.tracking_number ? ` with tracking ${f.labels[0].tracking_number}` : ""}.` })
      if (f.delivered_at) out.push({ at: f.delivered_at, text: "Marked as delivered." })
      if (f.canceled_at) out.push({ at: f.canceled_at, text: "Fulfillment canceled." })
    }
    if (order.canceled_at) out.push({ at: order.canceled_at, text: "Order canceled." })
    for (const a of data?.activity ?? []) {
      if (a.action === "orders.comment") out.push({ at: a.created_at, text: (a.metadata as any)?.body ?? "", by: a.actor_email, comment: true })
      else if (a.action === "orders.update" && a.status_code && a.status_code < 300) out.push({ at: a.created_at, text: "Order details edited.", by: a.actor_email })
    }
    return out.filter((e) => e.at).sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
  }, [order, data])

  return (
    <div>
      <h2 className="mb-2 text-[14px] font-semibold">Timeline</h2>
      {canWrite && (
        <Section>
          <form
            className="flex flex-col gap-2 p-3 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault()
              if (!text.trim()) return
              add.mutate(text.trim(), { onSuccess: () => setText(""), onError: (err) => toast.error(errorMessage(err)) })
            }}
          >
            <Input value={text} maxLength={5000} onChange={(e) => setText(e.target.value)} placeholder="Leave a comment…" className="flex-1" />
            <Button size="small" type="submit" disabled={!text.trim()} isLoading={add.isPending}>
              Post
            </Button>
          </form>
        </Section>
      )}
      <ol className="mt-3 flex flex-col gap-y-3 border-l border-ui-border-strong pl-4">
        {events.map((e, i) => (
          <li key={i} className="relative">
            <span className={"absolute -left-[21px] top-1.5 size-2.5 rounded-full border-2 border-ui-bg-subtle " + (e.comment ? "bg-[#005bd3]" : "bg-ui-border-strong")} />
            <div className={e.comment ? "rounded-lg bg-ui-bg-base p-2 shadow-elevation-card-rest" : ""}>
              <Text size="small" className="whitespace-pre-wrap break-words">{e.text}</Text>
              <Text size="xsmall" className="text-ui-fg-muted">
                {formatDate(e.at)}
                {e.by ? ` · ${e.by}` : ""}
              </Text>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

function NotesCard({ order, canWrite }: { order: any; canWrite: boolean }) {
  const save = useUpdateOrderMeta(order.id)
  const [editing, setEditing] = useState(false)
  const [note, setNote] = useState(order.metadata?.note ?? "")
  const customerNote = order.metadata?.customer_note
  return (
    <Section title="Notes" actions={canWrite && !editing && <Button size="small" variant="transparent" onClick={() => setEditing(true)}>Edit</Button>}>
      <div className="px-4 py-3">
        {editing ? (
          <div className="flex flex-col gap-2">
            <Textarea rows={4} value={note} maxLength={5000} onChange={(e) => setNote(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button size="small" variant="secondary" onClick={() => (setNote(order.metadata?.note ?? ""), setEditing(false))}>Cancel</Button>
              <Button
                size="small"
                isLoading={save.isPending}
                onClick={() => save.mutate({ ...(order.metadata ?? {}), note: note.trim() || null }, { onSuccess: () => (toast.success("Note saved"), setEditing(false)), onError: (e) => toast.error(errorMessage(e)) })}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <Text size="small" className={order.metadata?.note ? "whitespace-pre-wrap" : "text-ui-fg-muted"}>{order.metadata?.note || "No notes"}</Text>
        )}
        {customerNote && <Text size="small" className="mt-2 text-ui-fg-subtle">Customer: {customerNote}</Text>}
      </div>
    </Section>
  )
}

function Address({ a }: { a: any }) {
  return <AddressText address={a} />
}

const sameAddress = (a: any, b: any) => a && b && ["first_name", "last_name", "address_1", "address_2", "city", "province", "postal_code", "country_code"].every((k) => (a[k] ?? "") === (b[k] ?? ""))

function CustomerCard({ order }: { order: any }) {
  const c = order.customer
  return (
    <Section title="Customer">
      <div className="px-4 py-3">
        {c ? (
          <Link to={`/customers/${c.id}`} className="text-[13px] font-medium text-ui-fg-interactive hover:underline">
            {fullName(c) || c.email}
          </Link>
        ) : (
          <Text size="small">{fullName(order.shipping_address) || "Guest"}</Text>
        )}
      </div>
      <div className="flex flex-col gap-y-1 px-4 py-3">
        <Text size="small" weight="plus">Contact information</Text>
        <Text size="small" className="break-all text-ui-fg-interactive">{order.email ?? "No email"}</Text>
        <Text size="small" className="text-ui-fg-subtle">{order.shipping_address?.phone || c?.phone || "No phone number"}</Text>
      </div>
      <div className="flex flex-col gap-y-1 px-4 py-3">
        <Text size="small" weight="plus">Shipping address</Text>
        {order.shipping_address ? <Address a={order.shipping_address} /> : <Text size="small" className="text-ui-fg-muted">No shipping address</Text>}
      </div>
      <div className="flex flex-col gap-y-1 px-4 py-3">
        <Text size="small" weight="plus">Billing address</Text>
        {!order.billing_address ? (
          <Text size="small" className="text-ui-fg-muted">No billing address</Text>
        ) : sameAddress(order.billing_address, order.shipping_address) ? (
          <Text size="small" className="text-ui-fg-subtle">Same as shipping address</Text>
        ) : (
          <Address a={order.billing_address} />
        )}
      </div>
    </Section>
  )
}

/**
 * Where the order came from under the client's model: the last paid click (click ID or paid UTM
 * medium) in the 7 days before the order, from order.metadata.terify_attribution.last_click (backend
 * lib/analytics/touch.ts). Never platform-reported conversions; a Meta click with only fbclid →
 * Organic social; no click → Unattributed.
 */
export function attributionLine(lc: any): { title: string; detail: string | null } {
  if (!lc) return { title: "Not recorded", detail: "Placed before last-click attribution was switched on." }
  if (lc.status === "renewal") return { title: "Subscription renewal", detail: "Charged automatically; never credited to ads." }
  if (lc.status === "draft") return { title: "Draft order", detail: "Made by staff; never credited to ads." }
  if (lc.status === "organic_social") return { title: "Organic social", detail: "A Facebook / Instagram link without ad parameters (no paid_social UTM medium or ad ID) in the 7 days before this order; not credited to Meta ads." }
  if (!lc.attributed) return { title: "Unattributed", detail: "No paid click (click ID or paid UTM) in the 7 days before this order." }
  const campaign = lc.utm_campaign || (lc.campaign_id ? `campaign ${lc.campaign_id}` : null)
  const days = lc.days_before == null ? null : lc.days_before === 0 ? "same day" : `${lc.days_before} day${lc.days_before === 1 ? "" : "s"} before order`
  return { title: ["Last click: " + (lc.label ?? "Other"), campaign && (lc.utm_campaign ? `campaign ${campaign}` : campaign), days].filter(Boolean).join(" · "), detail: null }
}

function AttributionCard({ order }: { order: any }) {
  const lc = order.metadata?.terify_attribution?.last_click
  const line = attributionLine(lc)
  const rows: [string, string][] = lc?.attributed
    ? ([
        ["Click", lc.click_type ?? "UTM tags (no click ID)"],
        ["UTM source / medium", [lc.utm_source, lc.utm_medium].filter(Boolean).join(" / ") || "—"],
        ["Ad (UTM content)", lc.utm_content ?? "—"],
        ["Campaign ID", lc.campaign_id ?? "—"],
        ["Ad set ID", lc.adset_id ?? "—"],
        ["Ad ID", lc.ad_id ?? "—"],
        ["Clicked", lc.touch_at ? formatDate(lc.touch_at) : "—"],
      ] as [string, string][]).filter(([, v]) => v !== "—")
    : []
  return (
    <Section title="Attribution">
      <div className="flex flex-col gap-y-1 px-4 py-3" data-testid="attribution-card">
        <Text size="small" weight="plus" data-testid="attribution-line">{line.title}</Text>
        {line.detail && <Text size="xsmall" className="text-ui-fg-subtle">{line.detail}</Text>}
        {rows.length > 0 && (
          <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12px]">
            {rows.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-ui-fg-subtle">{k}</dt>
                <dd className="min-w-0 break-words">{v}</dd>
              </div>
            ))}
          </dl>
        )}
        <Text size="xsmall" className="text-ui-fg-muted">Last click, 7 days, from the order's own UTM tags and click IDs.</Text>
      </div>
    </Section>
  )
}

function TagsCard({ order, canWrite }: { order: any; canWrite: boolean }) {
  const save = useUpdateOrderMeta(order.id)
  const tags: string[] = Array.isArray(order.metadata?.tags) ? order.metadata.tags : []
  const [text, setText] = useState("")
  const commit = (next: string[]) => save.mutate({ ...(order.metadata ?? {}), tags: next }, { onError: (e) => toast.error(errorMessage(e)) })
  return (
    <Section title="Tags">
      <div className="flex flex-col gap-2 px-4 py-3">
        {canWrite && (
          <Input
            size="small"
            value={text}
            placeholder="Add tags"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault()
                const t = text.trim().slice(0, 40)
                if (t && !tags.some((x) => x.toLowerCase() === t.toLowerCase())) commit([...tags, t])
                setText("")
              }
            }}
          />
        )}
        <div className="flex flex-wrap gap-1">
          {tags.length ? (
            tags.map((t) => (
              <span key={t} className="tf-pill bg-ui-bg-component text-ui-fg-base">
                {t}
                {canWrite && (
                  <button aria-label={`Remove ${t}`} className="ml-1 text-ui-fg-muted" onClick={() => commit(tags.filter((x) => x !== t))}>
                    ×
                  </button>
                )}
              </span>
            ))
          ) : (
            !canWrite && <Text size="small" className="text-ui-fg-muted">No tags</Text>
          )}
        </div>
      </div>
    </Section>
  )
}


/** "Subscription · every month" on a subscribe-and-save line; "Subscription renewal" on a renewal line. */
function subLabel(i: any): string | null {
  const m = i?.metadata?.terify_subscription
  if (m?.plan_id) {
    const n = Number(m.interval_count) || 1
    const unit = String(m.interval_unit || "month")
    return `Subscription · every ${n === 1 ? unit : `${n} ${unit}s`}`
  }
  if (i?.metadata?.terify_subscription_id) return "Subscription renewal"
  return null
}

const discountCodes = (o: any): string[] => [...new Set<string>((o.items ?? []).flatMap((i: any) => (i.adjustments ?? []).map((a: any) => a?.code).filter(Boolean)))]

/** What the customer was charged at checkout; Medusa's own total drops to 0 once an order is canceled. */
const orderTotal = (o: any) => {
  const placed = Number(o.summary?.original_order_total)
  return Number.isFinite(placed) && placed > 0 ? placed : Number(o.total) > 0 ? Number(o.total) : Number(o.original_total ?? 0)
}

/** The subscriptions behind this order: started by it, or renewed by it. */
function SubscriptionCard({ order }: { order: any }) {
  const kind = kindOf(order)
  const { data } = useOrderSubscriptions(order.id, kind !== "one_time" || (order.items ?? []).some((i: any) => i?.metadata?.terify_subscription_id))
  const subs = data?.subscriptions ?? []
  if (kind === "one_time" && !subs.length) return null
  return (
    <Section title={kind === "renewal" ? "Subscription renewal" : "Subscription"}>
      <div className="flex flex-col gap-y-1 px-4 py-3">
        <Text size="small" className="text-ui-fg-subtle">
          {kind === "renewal"
            ? "This order was charged automatically by the Subscriptions app."
            : "This order started the subscription below. Later orders are charged automatically."}
        </Text>
        {data?.charge && (
          <Link to={`/apps/subscriptions/payments/${data.charge.id}`} className="text-[13px] text-ui-fg-interactive hover:underline">
            View the payment{data.charge.attempts > 1 ? ` (${data.charge.attempts} attempts)` : ""}
          </Link>
        )}
      </div>
      {subs.length ? (
        <ul className="divide-y">
          {subs.map((s) => (
            <li key={s.id} className="flex flex-col gap-y-0.5 px-4 py-3">
              <Link to={`/apps/subscriptions/subscriptions/${s.id}`} className="text-[13px] font-medium text-ui-fg-interactive hover:underline">
                {s.title}{s.variant_title && s.variant_title !== "Default Title" ? ` · ${s.variant_title}` : ""}
              </Link>
              <span className="flex flex-wrap items-center gap-2 text-[12px] text-ui-fg-subtle">
                <Status value={s.status} />
                every {Number(s.interval_count) === 1 ? s.interval_unit : `${s.interval_count} ${s.interval_unit}s`} · {formatMoney(Number(s.unit_price) * Number(s.quantity), s.currency_code || order.currency_code)}
                {s.next_charge_at ? ` · next ${formatDate(s.next_charge_at)}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-4 py-3"><Text size="small" className="text-ui-fg-muted">{data ? "Subscription details aren't available." : "Loading…"}</Text></div>
      )}
    </Section>
  )
}

/**
 * Authorize.net settlement of the order's card payment (order.metadata.anet_settlement, written by the
 * settlement sync every 30 minutes): "Settled · batch 123456789 · Nov 3" once its batch settled,
 * "Unsettled (pending settlement)" before. Nothing for a payment that was never captured.
 */
function SettlementLine({ stamp, captured, voided }: { stamp?: SettlementStamp | null; captured: boolean; voided: boolean }) {
  const tz = useStoreTz()
  if (stamp?.status === "settled") {
    const day = stamp.settled_at ? new Date(stamp.settled_at).toLocaleDateString("en-US", { timeZone: tz, month: "short", day: "numeric" }) : null
    return (
      <span className="text-[12px]" data-testid="anet-settlement">
        <span className="text-[#0c5132]">Settled</span>
        <span className="text-ui-fg-subtle"> · batch {stamp.batch_id}{day ? ` · ${day}` : ""}{stamp.batch_ids && stamp.batch_ids.length > 1 ? ` (batches ${stamp.batch_ids.join(", ")})` : ""}</span>
      </span>
    )
  }
  if (stamp?.status === "voided" || (voided && !stamp)) return <span className="text-[12px] text-ui-fg-subtle" data-testid="anet-settlement">Voided in Authorize.net (never settled)</span>
  if (stamp?.status === "authorized") return <span className="text-[12px] text-ui-fg-subtle" data-testid="anet-settlement">Authorized, not captured (settles after capture)</span>
  if (stamp?.status === "unsettled" || captured) return <span className="text-[12px] text-[#8a6100]" data-testid="anet-settlement">Unsettled (pending settlement)</span>
  return null
}

/** Authorize.net address-verification result, in words. */
function avsLabel(code?: string | null) {
  const m: Record<string, string> = { Y: "address + ZIP match", X: "address + ZIP match", A: "address matches, ZIP doesn't", W: "ZIP matches, address doesn't", Z: "ZIP matches, address doesn't", N: "address and ZIP don't match", P: "AVS not applicable", S: "AVS not supported", U: "address unavailable", R: "AVS retry", G: "non-US card", B: "address not provided", E: "AVS error" }
  return code ? `AVS: ${m[code] ?? code}` : "AVS: —"
}
function cvvLabel(code?: string | null) {
  const m: Record<string, string> = { M: "match", N: "no match", P: "not processed", S: "should be on card", U: "issuer not certified" }
  return code ? `CVV: ${m[code] ?? code}` : "CVV: —"
}

/** Open problems for this order from admin → Issues (a failed shipment, refund, email…). */
function OrderIssues({ orderId }: { orderId: string }) {
  const { data } = useIssues(new URLSearchParams({ status: "open", order_id: orderId, limit: "10" }).toString())
  const list = data?.issues ?? []
  if (!list.length) return null
  const money = list.some((i) => i.severity === "critical")
  return (
    <div className={`mb-4 rounded-[12px] border px-4 py-3 text-[13px] ${money ? "border-[#e0b4b4] bg-[#fff4f4]" : "border-[#ecd9a8] bg-[#fffaf0]"}`}>
      <p className="mb-1 font-semibold">{list.length === 1 ? "This order has a problem" : `This order has ${list.length} problems`}</p>
      <ul className="list-disc pl-5">
        {list.map((i) => (
          <li key={i.id}>{i.title}</li>
        ))}
      </ul>
      <Link className="mt-1 inline-block text-ui-fg-interactive hover:underline" to={`/issues?q=${encodeURIComponent(orderId)}`}>Open in Issues →</Link>
    </div>
  )
}
